import React from 'react';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Switch } from '../../ui/switch';
import { Checkbox } from '../../ui/checkbox';
import { Loader2, ChevronDown } from 'lucide-react';

// Reusable component for Channel/Inbox selection
const ChannelInboxSelector = ({
  formData,
  setFormData,
  errors,
  setErrors,
  handleInputChange,
  isDefaultChannel,
  setIsDefaultChannel,
  allInboxes,
  isLoadingInboxes,
  isLoadingChannels,
  channelInput,
  setChannelInput,
  channelSuggestions,
  showChannelSuggestions,
  setShowChannelSuggestions,
  setChannelSuggestions,
  selectedChannelSuggestionIndex,
  setSelectedChannelSuggestionIndex,
  handleChannelInputChange,
  handleInboxChannelInputChange,
  handleChannelInputKeyDown,
  handleChannelSuggestionClick,
  channelSuggestionsRef,
  inboxChannels,
  allChannels,
  setInboxChannels,
  setDefaultChannel,
  setIsLoadingChannels,
  fetchInboxChannels,
  isSubmitting,
  isDark,
  idSuffix
}) => {
  return (
    <div className={`rounded-[10px] p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
      isDark ? 'bg-[#161515]' : 'bg-[#F5F5F5]'
    }`}>
      {/* Toggle appears only after email is added */}
      {allInboxes.length > 0 && (
        <>
          <div className={`text-[16px] font-semibold transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>
            Please chose if you want to use default email or shared inbox
          </div>

          {/* Toggle Switch */}
          <div className={`flex items-center gap-[16px] p-[16px] rounded-[8px] w-fit transition-colors duration-200 ${
            isDark ? 'bg-[#000000]' : 'bg-[#ffffff]'
          }`}>
            <span className={`text-[16px] font-normal transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>Default Email</span>
            <Switch
              checked={isDefaultChannel}
              onCheckedChange={(checked) => {
                setIsDefaultChannel(checked);
                setChannelInput("");
                setFormData(prev => ({
                  ...prev,
                  channel_id: '',
                  channel_send_as: '',
                  inbox_id: '',
                }));
                if (checked) {
                  setDefaultChannel(allChannels);
                }
              }}
            />
            <span className={`text-[16px] font-normal transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>Shared Inbox</span>
          </div>
        </>
      )}

      {/* Shared Inbox Selection */}
      {isDefaultChannel && (
        <div className="flex flex-col gap-[16px]">
          <div className={`text-[16px] font-semibold transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>Select Shared Inbox</div>
          {isLoadingInboxes ? (
            <div className={`text-[16px] font-normal transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>Loading inboxes...</div>
          ) : allInboxes.filter(inbox => inbox.send_as).length === 0 ? (
            <div className={`text-[16px] font-normal transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>No shared inboxes</div>
          ) : (
            <div className="flex flex-col gap-[16px]">
              {allInboxes.filter(inbox => inbox.send_as).map((inbox) => (
                <div key={inbox.id} className="flex flex-col gap-[12px]">
                  <div className="flex items-center gap-[8px]">
                    <Checkbox
                      id={`inbox-${inbox.id}-${idSuffix}`}
                      checked={formData.inbox_id === inbox.id}
                      onCheckedChange={(checked) => {
                        setInboxChannels([]);
                        if (checked) {
                          handleInputChange('inbox_id', inbox.id);
                          setChannelInput('');
                          setFormData(prev => ({ ...prev, channel_id: '', channel_send_as: '' }));
                          setErrors(prev => ({ ...prev, inbox: false }));
                          setIsLoadingChannels(true);
                          fetchInboxChannels(inbox.id).then(channels => {
                            setInboxChannels(channels);
                            setDefaultChannel(channels);
                            setIsLoadingChannels(false);
                          });
                        } else {
                          handleInputChange('inbox_id', '');
                          setFormData(prev => ({ ...prev, channel_id: '', channel_send_as: '' }));
                        }
                      }}
                    />
                    <label htmlFor={`inbox-${inbox.id}-${idSuffix}`} className={`text-[16px] font-normal cursor-pointer transition-colors duration-200 ${
                      isDark ? 'text-white' : 'text-[#072837]'
                    }`}>
                      {inbox.name} <span className={`text-[16px] transition-colors duration-200 ${
                        isDark ? 'text-[#b3b3b3]' : 'text-[#72837]'
                      }`}>({inbox.send_as})</span>
                    </label>
                  </div>

                  {/* Channel Dropdown for selected inbox */}
                  {formData.inbox_id === inbox.id && (
                    <div className="w-full">
                      <div className={`text-[16px] font-semibold transition-colors duration-200 ${
                        isDark ? 'text-white' : 'text-[#072837]'
                      }`}>Select Email</div>
                      <div className="relative mt-[4px]" ref={channelSuggestionsRef}>
                        <input
                          className={`w-full px-[12px] py-[12px] rounded-[8px] text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] disabled:opacity-50 transition-colors duration-200 ${
                            isDark
                              ? 'bg-[#000000] text-white placeholder:text-[#b3b3b3] border-none'
                              : 'bg-white text-[#072837] placeholder:text-[#686868] border-none'
                          }`}
                          type="text"
                          placeholder="Select Email"
                          value={channelInput}
                          disabled={isSubmitting || !formData.email || formData.email.trim() === ''}
                          onChange={handleInboxChannelInputChange}
                          onFocus={() => {
                            if (channelInput.length === 0) {
                              const allWithSendAs = inboxChannels.filter(channel => channel.send_as);
                              setChannelSuggestions(allWithSendAs);
                              setShowChannelSuggestions(allWithSendAs.length > 0);
                              setSelectedChannelSuggestionIndex(-1);
                            } else {
                              setShowChannelSuggestions(true);
                            }
                          }}
                          onKeyDown={handleChannelInputKeyDown}
                          autoComplete="off"
                        />
                        <span
                          className="absolute right-0 top-0 h-full flex items-center px-[12px] cursor-pointer transition-transform z-[2]"
                          style={{ transform: `rotate(${showChannelSuggestions ? '180deg' : '0deg'})` }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (showChannelSuggestions) {
                              setShowChannelSuggestions(false);
                            } else {
                              const allWithSendAs = inboxChannels.filter(channel => channel.send_as);
                              setChannelSuggestions(allWithSendAs);
                              setShowChannelSuggestions(allWithSendAs.length > 0);
                              setSelectedChannelSuggestionIndex(-1);
                            }
                          }}
                        >
                          {isLoadingChannels ? (
                            <Loader2 className={`h-[18px] w-[18px] animate-spin transition-colors duration-200 ${
                              isDark ? 'text-[#b3b3b3]' : 'text-[#555]'
                            }`} />
                          ) : (
                            <ChevronDown className={`h-[18px] w-[18px] transition-colors duration-200 ${
                              channelSuggestions && channelSuggestions.length > 0
                                ? isDark ? 'text-white' : 'text-[#000]'
                                : isDark ? 'text-[#8c8c8c]' : 'text-[#898989]'
                            }`} />
                          )}
                        </span>

                        {showChannelSuggestions && channelSuggestions.length > 0 && (
                          <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
                            isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
                          }`}>
                            {channelSuggestions.map((channel, idx) => (
                              <div
                                key={channel.id}
                                className={`px-3 py-2 font-normal text-[16px] cursor-pointer flex items-center justify-between group transition-colors duration-200 ${
                                  idx === selectedChannelSuggestionIndex
                                    ? isDark
                                      ? 'bg-[#cd6d2c] text-[#000000]'
                                      : 'bg-[#e9ecef] text-[#072837]'
                                    : isDark
                                      ? 'bg-[#161515] hover:bg-[#292929] text-white'
                                      : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                                }`}
                                onMouseDown={() => handleChannelSuggestionClick(channel)}
                              >
                                <div className={`flex items-center gap-[8px] ${
                                  idx === selectedChannelSuggestionIndex
                                    ? 'text-[#000000]'
                                    : isDark
                                      ? 'text-white group-hover:!text-white'
                                      : 'group-hover:!text-white text-[#072837]'
                                }`}>
                                  {channel.name} <span className={`text-[16px] transition-colors duration-200 ${
                                    idx === selectedChannelSuggestionIndex
                                      ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                                      : isDark ? 'text-[#b3b3b3] group-hover:!text-white' : 'group-hover:!text-white text-[#686868]'
                                  }`}>({channel.send_as})</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Default Channel Selection (when not using shared inbox) */}
      {!isDefaultChannel && (
        <div className="flex flex-col gap-[16px] pb-[6px]">
          <div className={`text-[16px] font-semibold transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>Select Email</div>
          <div className="relative" ref={channelSuggestionsRef}>
            <input
              className={`w-full px-[12px] py-[12px] pr-[40px] rounded-[8px] text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] disabled:opacity-50 transition-colors duration-200 ${
                isDark
                  ? 'bg-[#000000] text-white placeholder:text-[#b3b3b3] border-none'
                  : 'bg-white text-[#072837] placeholder:text-[#686868] border-none'
              }`}
              type="text"
              placeholder={allChannels.length > 0 ? "Select Email" : "No Email Found"}
              value={channelInput}
              disabled={isSubmitting || !formData.email || formData.email.trim() === ''}
              onChange={handleChannelInputChange}
              onFocus={() => {
                if (channelInput.length === 0) {
                  const allWithSendAs = allChannels.filter(channel => channel.send_as);
                  setChannelSuggestions(allWithSendAs);
                  setShowChannelSuggestions(allWithSendAs.length > 0);
                  setSelectedChannelSuggestionIndex(-1);
                } else {
                  setShowChannelSuggestions(true);
                }
              }}
              onKeyDown={handleChannelInputKeyDown}
              autoComplete="off"
            />
            <span
              className="absolute right-0 top-0 h-full flex items-center px-[12px] cursor-pointer transition-transform z-[2]"
              style={{ transform: `rotate(${showChannelSuggestions ? '180deg' : '0deg'})` }}
              onMouseDown={(e) => {
                e.preventDefault();
                if (showChannelSuggestions) {
                  setShowChannelSuggestions(false);
                } else {
                  const allWithSendAs = allChannels.filter(channel => channel.send_as);
                  setChannelSuggestions(allWithSendAs);
                  setShowChannelSuggestions(allWithSendAs.length > 0);
                  setSelectedChannelSuggestionIndex(-1);
                }
              }}
            >
              {isLoadingChannels ? (
                <Loader2 className={`h-[18px] w-[18px] animate-spin transition-colors duration-200 ${
                  isDark ? 'text-[#b3b3b3]' : 'text-[#555]'
                }`} />
              ) : (
                <ChevronDown className={`h-[18px] w-[18px] transition-colors duration-200 ${
                  channelSuggestions && channelSuggestions.length > 0
                    ? isDark ? 'text-white' : 'text-[#000]'
                    : isDark ? 'text-[#8c8c8c]' : 'text-[#898989]'
                }`} />
              )}
            </span>
            {showChannelSuggestions && channelSuggestions.length > 0 && (
              <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
                isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
              }`}>
                {channelSuggestions.map((channel, idx) => (
                  <div
                    key={channel.id}
                    className={`px-3 py-2 font-normal text-[16px] cursor-pointer flex items-center justify-between group transition-colors duration-200 ${
                      idx === selectedChannelSuggestionIndex
                        ? isDark
                          ? 'bg-[#cd6d2c] text-[#000000]'
                          : 'bg-[#e9ecef] text-[#072837]'
                        : isDark
                          ? 'bg-[#161515] hover:bg-[#292929] text-white'
                          : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                    }`}
                    onMouseDown={() => handleChannelSuggestionClick(channel)}
                  >
                    <div className={`flex items-center gap-[8px] ${
                      idx === selectedChannelSuggestionIndex
                        ? 'text-[#000000]'
                        : isDark
                          ? 'text-white group-hover:!text-white'
                          : 'group-hover:!text-white text-[#072837]'
                    }`}>
                      {channel.name} <span className={`text-[16px] transition-colors duration-200 ${
                        idx === selectedChannelSuggestionIndex
                          ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                          : isDark ? 'text-[#b3b3b3] group-hover:!text-white' : 'group-hover:!text-white text-[#686868]'
                      }`}>({channel.send_as})</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ManagerEmailSection = ({
  formData,
  setFormData,
  errors,
  setErrors,
  handleInputChange,
  showEmailField,
  showEmailSuggestions,
  emailSuggestions,
  selectedSuggestionIndex,
  handleEmailSearch,
  handleEmailKeyDown,
  handleEmailSelect,
  emailSuggestionsRef,
  showSignatureSelect,
  isLoadingSignatures,
  emailSignatures,
  userEmailSignatures,
  isDefaultChannel,
  setIsDefaultChannel,
  allInboxes,
  isLoadingInboxes,
  isLoadingChannels,
  channelInput,
  setChannelInput,
  channelSuggestions,
  showChannelSuggestions,
  setShowChannelSuggestions,
  setChannelSuggestions,
  selectedChannelSuggestionIndex,
  setSelectedChannelSuggestionIndex,
  handleChannelInputChange,
  handleInboxChannelInputChange,
  handleChannelInputKeyDown,
  handleChannelSuggestionClick,
  channelSuggestionsRef,
  inboxChannels,
  allChannels,
  setInboxChannels,
  setDefaultChannel,
  setIsLoadingChannels,
  fetchInboxChannels,
  isSubmitting,
  isDark
}) => {
  return (
    <div className={`border-none rounded-[16px] p-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] w-full max-w-[822px] flex flex-col gap-[16px] transition-colors duration-200 ${
      isDark ? 'bg-[#000000]' : 'bg-white'
    } ${errors.email || errors.emailSuggestion || (isDefaultChannel && errors.inbox) || errors.channelSuggestion ? 'border-[#FF0000]' : ''}`}>
      {/* Header */}
      <div className={`text-[16px] font-semibold transition-colors duration-200 ${
        isDark ? 'text-white' : 'text-[#072837]'
      }`}>
        Add email address of Manager/Assist <span className="text-[#FF0000]">*</span>
      </div>

      {/* Radio buttons for Sending from Me / Sending from Others */}
      <RadioGroup
        value={formData.sendingOption || ''}
        onValueChange={(value) => handleInputChange('sendingOption', value)}
        className="flex gap-[24px]"
      >
        <label className={`flex items-center gap-[8px] cursor-pointer transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>
          <RadioGroupItem value="sending-from-me" id="sendingOption-sending-from-me" />
          <span className={`text-[16px] font-normal transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>Sending from Me</span>
        </label>

        <label className={`flex items-center gap-[8px] cursor-pointer transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>
          <RadioGroupItem value="sending-from-other" id="sendingOption-sending-from-other" />
          <span className={`text-[16px] font-normal transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>Sending from Others</span>
        </label>
      </RadioGroup>

      {/* Email input field with suggestions */}
      {showEmailField && (
        <div className="relative" ref={emailSuggestionsRef}>
          <input
            className={`w-full px-[12px] py-[12px] rounded-[8px] text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] transition-colors duration-200 ${
              isDark
                ? 'bg-[#161515] text-white placeholder:text-[#b3b3b3] border-none'
                : 'bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa] text-[#072837] placeholder:text-[#686868]'
            }`}
            type="text"
            placeholder="Enter email"
            value={formData.email}
            onChange={(e) => handleEmailSearch(e.target.value)}
            onKeyDown={handleEmailKeyDown}
          />
          {showEmailSuggestions && emailSuggestions.length > 0 && (
            <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
              isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
            }`}>
              {emailSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={`px-3 py-2 font-medium text-[14px] cursor-pointer flex items-center group transition-colors duration-200 ${
                    index === selectedSuggestionIndex 
                      ? isDark 
                        ? 'bg-[#cd6d2c] text-[#000000]' 
                        : 'bg-[#e9ecef] text-[#072837]'
                      : isDark 
                        ? 'bg-[#161515] hover:bg-[#292929] text-white' 
                        : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                  }`}
                  onClick={() => handleEmailSelect(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Signature Selection */}
      {showSignatureSelect && (
        <div className="flex flex-col gap-[16px]">
          <div className={`text-[16px] font-semibold transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>Select Email Signature</div>
          {isLoadingSignatures ? (
            <div className={`text-[16px] font-normal p-[8px] transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>Loading signatures...</div>
          ) : (
            <RadioGroup
              value={formData.signature_id || ''}
              onValueChange={(value) => handleInputChange('signature_id', value)}
              className="flex flex-wrap gap-[16px]"
            >
              {(formData.sendingOption === 'sending-from-me' ? userEmailSignatures : emailSignatures).map((signature) => (
                <label
                  key={signature.id}
                  className={`flex items-center gap-[8px] px-[12px] py-[10px] rounded-[8px] cursor-pointer transition-colors duration-200 ${
                    isDark
                      ? 'bg-[#161515] text-white hover:bg-[#292929]'
                      : 'bg-[#f5f5f5] text-[#072837] hover:bg-[#e9ecef]'
                  }`}
                >
                  <RadioGroupItem value={signature.id?.toString() || ''} id={`signature-${signature.id}`} />
                  <span className="text-[16px] font-normal">
                    {signature.name || `Signature ${signature.id}`}
                  </span>
                </label>
              ))}
            </RadioGroup>
          )}
        </div>
      )}

      {/* Channel Section - Appears when Sending from Me is selected */}
      {formData.sendingOption === 'sending-from-me' && (
        <div className={`rounded-[10px] p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
          isDark ? 'bg-[#161515]' : 'bg-[#F5F5F5]'
        }`}>
          {/* Toggle appears only after email is added */}
          {allInboxes.length > 0 && (
            <>
              <div className={`text-[16px] font-semibold transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>
                Please chose if you want to use default email or shared inbox
              </div>

              {/* Toggle Switch */}
              <div className={`flex items-center gap-[16px] p-[16px] rounded-[8px] w-fit transition-colors duration-200 ${
                isDark ? 'bg-[#000000]' : 'bg-[#ffffff]'
              }`}>
                <span className={`text-[16px] font-normal transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>Default Email</span>
                <Switch
                  checked={isDefaultChannel}
                  onCheckedChange={(checked) => {
                    setIsDefaultChannel(checked);
                    setChannelInput("");
                    setFormData(prev => ({
                      ...prev,
                      channel_id: '',
                      channel_send_as: '',
                      inbox_id: '',
                    }));
                    if (checked) {
                      setDefaultChannel(allChannels);
                    }
                  }}
                />
                <span className={`text-[16px] font-normal transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>Shared Inbox</span>
              </div>
            </>
          )}

          {/* Shared Inbox Selection */}
          {isDefaultChannel && (
            <div className="flex flex-col gap-[16px]">
              <div className={`text-[16px] font-semibold transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>Select Shared Inbox</div>
              {isLoadingInboxes ? (
                <div className={`text-[16px] font-normal transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>Loading inboxes...</div>
              ) : allInboxes.filter(inbox => inbox.send_as).length === 0 ? (
                <div className={`text-[16px] font-normal transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>No shared inboxes</div>
              ) : (
                <div className="flex flex-col gap-[16px]">
                  {allInboxes.filter(inbox => inbox.send_as).map((inbox) => (
                    <div key={inbox.id} className="flex flex-col gap-[12px]">
                      <div className="flex items-center gap-[8px]">
                          <Checkbox
                          id={`inbox-${inbox.id}-me`}
                          checked={formData.inbox_id === inbox.id}
                          isDark={isDark}
                          onCheckedChange={(checked) => {
                            setInboxChannels([]);
                            if (checked) {
                              handleInputChange('inbox_id', inbox.id);
                              setChannelInput('');
                              setFormData(prev => ({ ...prev, channel_id: '', channel_send_as: '' }));
                              setErrors(prev => ({ ...prev, inbox: false }));
                              setIsLoadingChannels(true);
                              fetchInboxChannels(inbox.id).then(channels => {
                                setInboxChannels(channels);
                                setDefaultChannel(channels);
                                setIsLoadingChannels(false);
                              });
                            } else {
                              handleInputChange('inbox_id', '');
                              setFormData(prev => ({ ...prev, channel_id: '', channel_send_as: '' }));
                            }
                          }}
                        />
                        <label htmlFor={`inbox-${inbox.id}-me`} className={`text-[16px] font-normal cursor-pointer transition-colors duration-200 ${
                          isDark ? 'text-white' : 'text-[#072837]'
                        }`}>
                          {inbox.name} <span className={`text-[16px] transition-colors duration-200 ${
                            isDark ? 'text-[#b3b3b3]' : 'text-[#72837]'
                          }`}>({inbox.send_as})</span>
                        </label>
                      </div>

                       {/* Channel Dropdown for selected inbox */}
                       {formData.inbox_id === inbox.id && (
                         <div className="w-full">
                           <div className={`text-[16px] font-semibold transition-colors duration-200 ${
                             isDark ? 'text-white' : 'text-[#072837]'
                           }`}>Select Email</div>
                           <div className="relative mt-[4px]" ref={channelSuggestionsRef}>
                             <input
                               className={`w-full px-[12px] py-[12px] rounded-[8px] text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] disabled:opacity-50 transition-colors duration-200 ${
                                 isDark
                                   ? 'bg-[#000000] text-white placeholder:text-[#b3b3b3] border-none'
                                   : 'bg-white text-[#072837] placeholder:text-[#686868] border-none'
                               }`}
                               type="text"
                               placeholder="Select Email"
                               value={channelInput}
                               disabled={isSubmitting || !formData.email || formData.email.trim() === ''}
                               onChange={handleInboxChannelInputChange}
                               onFocus={() => {
                                 if (channelInput.length === 0) {
                                   const allWithSendAs = inboxChannels.filter(channel => channel.send_as);
                                   setChannelSuggestions(allWithSendAs);
                                   setShowChannelSuggestions(allWithSendAs.length > 0);
                                   setSelectedChannelSuggestionIndex(-1);
                                 } else {
                                   setShowChannelSuggestions(true);
                                 }
                               }}
                               onKeyDown={handleChannelInputKeyDown}
                               autoComplete="off"
                             />
                             <span
                               className="absolute right-0 top-0 h-full flex items-center px-[12px] cursor-pointer transition-transform z-[2]"
                               style={{ transform: `rotate(${showChannelSuggestions ? '180deg' : '0deg'})` }}
                               onMouseDown={(e) => {
                                 e.preventDefault();
                                 if (showChannelSuggestions) {
                                   setShowChannelSuggestions(false);
                                 } else {
                                   const allWithSendAs = inboxChannels.filter(channel => channel.send_as);
                                   setChannelSuggestions(allWithSendAs);
                                   setShowChannelSuggestions(allWithSendAs.length > 0);
                                   setSelectedChannelSuggestionIndex(-1);
                                 }
                               }}
                             >
                               {isLoadingChannels ? (
                                 <Loader2 className={`h-[18px] w-[18px] animate-spin transition-colors duration-200 ${
                                   isDark ? 'text-[#b3b3b3]' : 'text-[#555]'
                                 }`} />
                               ) : (
                                 <ChevronDown className={`h-[18px] w-[18px] transition-colors duration-200 ${
                                   channelSuggestions && channelSuggestions.length > 0
                                     ? isDark ? 'text-white' : 'text-[#000]'
                                     : isDark ? 'text-[#8c8c8c]' : 'text-[#898989]'
                                 }`} />
                               )}
                             </span>

                             {showChannelSuggestions && channelSuggestions.length > 0 && (
                               <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
                                 isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
                               }`}>
                                 {channelSuggestions.map((channel, idx) => (
                                   <div
                                     key={channel.id}
                                     className={`px-3 py-2 font-normal text-[16px] cursor-pointer flex items-center justify-between group transition-colors duration-200 ${
                                       idx === selectedChannelSuggestionIndex
                                         ? isDark
                                           ? 'bg-[#cd6d2c] text-[#000000]'
                                           : 'bg-[#e9ecef] text-[#072837]'
                                         : isDark
                                           ? 'bg-[#161515] hover:bg-[#292929] text-white'
                                           : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                                     }`}
                                     onMouseDown={() => handleChannelSuggestionClick(channel)}
                                   >
                                     <div className={`flex items-center gap-[8px] ${
                                       idx === selectedChannelSuggestionIndex
                                         ? 'text-[#000000]'
                                         : isDark
                                           ? 'text-white group-hover:!text-white'
                                           : 'group-hover:!text-white text-[#072837]'
                                     }`}>
                                       {channel.name} <span className={`text-[16px] transition-colors duration-200 ${
                                         idx === selectedChannelSuggestionIndex
                                           ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                                           : isDark ? 'text-[#b3b3b3] group-hover:!text-white' : 'group-hover:!text-white text-[#686868]'
                                       }`}>({channel.send_as})</span>
                                     </div>
                                   </div>
                                 ))}
                               </div>
                             )}
                           </div>
                         </div>
                       )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

           {/* Default Channel Selection (when not using shared inbox) */}
           {!isDefaultChannel && (
             <div className="flex flex-col gap-[16px] pb-[6px]">
               <div className={`text-[16px] font-semibold transition-colors duration-200 ${
                 isDark ? 'text-white' : 'text-[#072837]'
               }`}>Select Email</div>
               <div className="relative" ref={channelSuggestionsRef}>
                 <input
                   className={`w-full px-[12px] py-[12px] pr-[40px] rounded-[8px] text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] disabled:opacity-50 transition-colors duration-200 ${
                     isDark
                       ? 'bg-[#000000] text-white placeholder:text-[#b3b3b3] border-none'
                       : 'bg-white text-[#072837] placeholder:text-[#686868] border-none'
                   }`}
                   type="text"
                   placeholder={allChannels.length > 0 ? "Select Email" : "No Email Found"}
                   value={channelInput}
                   disabled={isSubmitting || !formData.email || formData.email.trim() === ''}
                   onChange={handleChannelInputChange}
                   onFocus={() => {
                     if (channelInput.length === 0) {
                       const allWithSendAs = allChannels.filter(channel => channel.send_as);
                       setChannelSuggestions(allWithSendAs);
                       setShowChannelSuggestions(allWithSendAs.length > 0);
                       setSelectedChannelSuggestionIndex(-1);
                     } else {
                       setShowChannelSuggestions(true);
                     }
                   }}
                   onKeyDown={handleChannelInputKeyDown}
                   autoComplete="off"
                 />
                 <span
                   className="absolute right-0 top-0 h-full flex items-center px-[12px] cursor-pointer transition-transform z-[2]"
                   style={{ transform: `rotate(${showChannelSuggestions ? '180deg' : '0deg'})` }}
                   onMouseDown={(e) => {
                     e.preventDefault();
                     if (showChannelSuggestions) {
                       setShowChannelSuggestions(false);
                     } else {
                       const allWithSendAs = allChannels.filter(channel => channel.send_as);
                       setChannelSuggestions(allWithSendAs);
                       setShowChannelSuggestions(allWithSendAs.length > 0);
                       setSelectedChannelSuggestionIndex(-1);
                     }
                   }}
                 >
                   {isLoadingChannels ? (
                     <Loader2 className={`h-[18px] w-[18px] animate-spin transition-colors duration-200 ${
                       isDark ? 'text-[#b3b3b3]' : 'text-[#555]'
                     }`} />
                   ) : (
                     <ChevronDown className={`h-[18px] w-[18px] transition-colors duration-200 ${
                       channelSuggestions && channelSuggestions.length > 0
                         ? isDark ? 'text-white' : 'text-[#000]'
                         : isDark ? 'text-[#8c8c8c]' : 'text-[#898989]'
                     }`} />
                   )}
                 </span>
                 {showChannelSuggestions && channelSuggestions.length > 0 && (
                   <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
                     isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
                   }`}>
                     {channelSuggestions.map((channel, idx) => (
                       <div
                         key={channel.id}
                         className={`px-3 py-2 font-normal text-[16px] cursor-pointer flex items-center justify-between group transition-colors duration-200 ${
                          idx === selectedChannelSuggestionIndex
                            ? isDark
                              ? 'bg-[#cd6d2c] text-[#000000]'
                              : 'bg-[#e9ecef] text-[#072837]'
                            : isDark
                              ? 'bg-[#161515] hover:bg-[#292929] text-white'
                              : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                        }`}
                         onMouseDown={() => handleChannelSuggestionClick(channel)}
                       >
                         <div className={`flex items-center gap-[8px] ${
                          idx === selectedChannelSuggestionIndex
                            ? 'text-[#000000]'
                            : isDark
                              ? 'text-white group-hover:!text-white'
                              : 'group-hover:!text-white text-[#072837]'
                        }`}>
                          {channel.name} <span className={`text-[16px] transition-colors duration-200 ${
                            idx === selectedChannelSuggestionIndex
                              ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                              : isDark ? 'text-[#b3b3b3] group-hover:!text-white' : 'group-hover:!text-white text-[#686868]'
                          }`}>({channel.send_as})</span>
                        </div>
                       </div>
                     ))}
                   </div>
                 )}
               </div>
             </div>
           )}
        </div>
      )}

      {/* Channel Section - Appears when Sending from Others is selected */}
      {formData.sendingOption === 'sending-from-other' && (
        <div className={`rounded-[10px] p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${
          isDark ? 'bg-[#161515]' : 'bg-[#F5F5F5]'
        }`}>
          {/* Toggle appears only after email is added */}
          {formData.email && formData.email.trim() !== '' && allInboxes.length > 0 && (
            <>
              <div className={`text-[16px] font-semibold transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>
                Please chose if you want to use default email or shared inbox
              </div>

              {/* Toggle Switch */}
              <div className={`flex items-center gap-[16px] p-[16px] rounded-[8px] w-fit transition-colors duration-200 ${
                isDark ? 'bg-[#000000]' : 'bg-[#ffffff]'
              }`}>
                <span className={`text-[16px] font-normal transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>Default Email</span>
                <Switch
                  checked={isDefaultChannel}
                  onCheckedChange={(checked) => {
                    setIsDefaultChannel(checked);
                    setChannelInput("");
                    setFormData(prev => ({
                      ...prev,
                      channel_id: '',
                      channel_send_as: '',
                      inbox_id: '',
                    }));
                    if (checked) {
                      setDefaultChannel(allChannels);
                    }
                  }}
                />
                <span className={`text-[16px] font-normal transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>Shared Inbox</span>
              </div>
            </>
          )}

          {/* Shared Inbox Selection */}
          {isDefaultChannel && (
            <div className="flex flex-col gap-[16px]">
              <div className={`text-[16px] font-semibold transition-colors duration-200 ${
                isDark ? 'text-white' : 'text-[#072837]'
              }`}>Select Shared Inbox</div>
              {isLoadingInboxes ? (
                <div className={`text-[16px] font-normal transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>Loading inboxes...</div>
              ) : allInboxes.filter(inbox => inbox.send_as).length === 0 ? (
                <div className={`text-[16px] font-normal transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>No shared inboxes</div>
              ) : (
                <div className="flex flex-col gap-[16px]">
                  {allInboxes.filter(inbox => inbox.send_as).map((inbox) => (
                    <div key={inbox.id} className="flex flex-col gap-[12px]">
                      <div className="flex items-center gap-[8px]">
                          <Checkbox
                          id={`inbox-${inbox.id}-other`}
                          checked={formData.inbox_id === inbox.id}
                          isDark={isDark}
                          onCheckedChange={(checked) => {
                            setInboxChannels([]);
                            if (checked) {
                              handleInputChange('inbox_id', inbox.id);
                              setChannelInput('');
                              setFormData(prev => ({ ...prev, channel_id: '', channel_send_as: '' }));
                              setErrors(prev => ({ ...prev, inbox: false }));
                              setIsLoadingChannels(true);
                              fetchInboxChannels(inbox.id).then(channels => {
                                setInboxChannels(channels);
                                setDefaultChannel(channels);
                                setIsLoadingChannels(false);
                              });
                            } else {
                              handleInputChange('inbox_id', '');
                              setFormData(prev => ({ ...prev, channel_id: '', channel_send_as: '' }));
                            }
                          }}
                        />
                        <label htmlFor={`inbox-${inbox.id}-other`} className={`text-[16px] font-normal cursor-pointer transition-colors duration-200 ${
                          isDark ? 'text-white' : 'text-[#072837]'
                        }`}>
                          {inbox.name} <span className={`text-[16px] transition-colors duration-200 ${
                            isDark ? 'text-[#b3b3b3]' : 'text-[#72837]'
                          }`}>({inbox.send_as})</span>
                        </label>
                      </div>

                       {/* Channel Dropdown for selected inbox */}
                       {formData.inbox_id === inbox.id && (
                         <div className="w-full">
                           <div className={`text-[16px] font-semibold transition-colors duration-200 ${
                             isDark ? 'text-white' : 'text-[#072837]'
                           }`}>Select Email</div>
                           <div className="relative mt-[4px]" ref={channelSuggestionsRef}>
                             <input
                               className={`w-full px-[12px] py-[12px] rounded-[8px] text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] disabled:opacity-50 transition-colors duration-200 ${
                                 isDark
                                   ? 'bg-[#000000] text-white placeholder:text-[#b3b3b3] border-none'
                                   : 'bg-white text-[#072837] placeholder:text-[#686868] border-none'
                               }`}
                               type="text"
                               placeholder="Select Email"
                               value={channelInput}
                               disabled={isSubmitting || !formData.email || formData.email.trim() === ''}
                               onChange={handleInboxChannelInputChange}
                               onFocus={() => {
                                 if (channelInput.length === 0) {
                                   const allWithSendAs = inboxChannels.filter(channel => channel.send_as);
                                   setChannelSuggestions(allWithSendAs);
                                   setShowChannelSuggestions(allWithSendAs.length > 0);
                                   setSelectedChannelSuggestionIndex(-1);
                                 } else {
                                   setShowChannelSuggestions(true);
                                 }
                               }}
                               onKeyDown={handleChannelInputKeyDown}
                               autoComplete="off"
                             />
                             <span
                               className="absolute right-0 top-0 h-full flex items-center px-[12px] cursor-pointer transition-transform z-[2]"
                               style={{ transform: `rotate(${showChannelSuggestions ? '180deg' : '0deg'})` }}
                               onMouseDown={(e) => {
                                 e.preventDefault();
                                 if (showChannelSuggestions) {
                                   setShowChannelSuggestions(false);
                                 } else {
                                   const allWithSendAs = inboxChannels.filter(channel => channel.send_as);
                                   setChannelSuggestions(allWithSendAs);
                                   setShowChannelSuggestions(allWithSendAs.length > 0);
                                   setSelectedChannelSuggestionIndex(-1);
                                 }
                               }}
                             >
                               {isLoadingChannels ? (
                                 <Loader2 className={`h-[18px] w-[18px] animate-spin transition-colors duration-200 ${
                                   isDark ? 'text-[#b3b3b3]' : 'text-[#555]'
                                 }`} />
                               ) : (
                                 <ChevronDown className={`h-[18px] w-[18px] transition-colors duration-200 ${
                                   channelSuggestions && channelSuggestions.length > 0 
                                     ? isDark ? 'text-white' : 'text-[#000]'
                                     : isDark ? 'text-[#8c8c8c]' : 'text-[#898989]'
                                 }`} />
                               )}
                             </span>

                             {showChannelSuggestions && channelSuggestions.length > 0 && (
                               <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
                                 isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
                               }`}>
                                 {channelSuggestions.map((channel, idx) => (
                                   <div
                                     key={channel.id}
                                     className={`px-3 py-2 font-normal text-[16px] cursor-pointer flex items-center justify-between group transition-colors duration-200 ${
                                       idx === selectedChannelSuggestionIndex 
                                         ? isDark 
                                           ? 'bg-[#cd6d2c] text-[#000000]' 
                                           : 'bg-[#e9ecef] text-[#072837]'
                                         : isDark 
                                           ? 'bg-[#161515] hover:bg-[#292929] text-white' 
                                           : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                                     }`}
                                     onMouseDown={() => handleChannelSuggestionClick(channel)}
                                   >
                                     <div className={`flex items-center gap-[8px] ${
                                       idx === selectedChannelSuggestionIndex 
                                         ? 'text-[#000000]' 
                                         : isDark 
                                           ? 'text-white group-hover:!text-white' 
                                           : 'group-hover:!text-white text-[#072837]'
                                     }`}>
                                       {channel.name} <span className={`text-[16px] transition-colors duration-200 ${
                                         idx === selectedChannelSuggestionIndex
                                           ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                                           : isDark ? 'text-[#b3b3b3] group-hover:!text-white' : 'group-hover:!text-white text-[#686868]'
                                       }`}>({channel.send_as})</span>
                                     </div>
                                   </div>
                                 ))}
                               </div>
                             )}
                           </div>
                         </div>
                       )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

           {/* Default Channel Selection (when not using shared inbox) */}
           {!isDefaultChannel && (
             <div className="flex flex-col gap-[16px] pb-[6px]">
               <div className={`text-[16px] font-semibold transition-colors duration-200 ${
                 isDark ? 'text-white' : 'text-[#072837]'
               }`}>Select Email</div>
               <div className="relative" ref={channelSuggestionsRef}>
                 <input
                   className={`w-full px-[12px] py-[12px] pr-[40px] rounded-[8px] text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] disabled:opacity-50 transition-colors duration-200 ${
                     isDark
                       ? 'bg-[#000000] text-white placeholder:text-[#b3b3b3] border-none'
                       : 'bg-white text-[#072837] placeholder:text-[#686868] border-none'
                   }`}
                   type="text"
                   placeholder={allChannels.length > 0 ? "Select Email" : "No Email Found"}
                   value={channelInput}
                   disabled={isSubmitting || !formData.email || formData.email.trim() === ''}
                   onChange={handleChannelInputChange}
                   onFocus={() => {
                     if (channelInput.length === 0) {
                       const allWithSendAs = allChannels.filter(channel => channel.send_as);
                       setChannelSuggestions(allWithSendAs);
                       setShowChannelSuggestions(allWithSendAs.length > 0);
                       setSelectedChannelSuggestionIndex(-1);
                     } else {
                       setShowChannelSuggestions(true);
                     }
                   }}
                   onKeyDown={handleChannelInputKeyDown}
                   autoComplete="off"
                 />
                 <span
                   className="absolute right-0 top-0 h-full flex items-center px-[12px] cursor-pointer transition-transform z-[2]"
                   style={{ transform: `rotate(${showChannelSuggestions ? '180deg' : '0deg'})` }}
                   onMouseDown={(e) => {
                     e.preventDefault();
                     if (showChannelSuggestions) {
                       setShowChannelSuggestions(false);
                     } else {
                       const allWithSendAs = allChannels.filter(channel => channel.send_as);
                       setChannelSuggestions(allWithSendAs);
                       setShowChannelSuggestions(allWithSendAs.length > 0);
                       setSelectedChannelSuggestionIndex(-1);
                     }
                   }}
                 >
                   {isLoadingChannels ? (
                     <Loader2 className={`h-[18px] w-[18px] animate-spin transition-colors duration-200 ${
                       isDark ? 'text-[#b3b3b3]' : 'text-[#555]'
                     }`} />
                   ) : (
                     <ChevronDown className={`h-[18px] w-[18px] transition-colors duration-200 ${
                       channelSuggestions && channelSuggestions.length > 0 
                         ? isDark ? 'text-white' : 'text-[#000]'
                         : isDark ? 'text-[#8c8c8c]' : 'text-[#898989]'
                     }`} />
                   )}
                 </span>
                 {showChannelSuggestions && channelSuggestions.length > 0 && (
                   <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
                     isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
                   }`}>
                     {channelSuggestions.map((channel, idx) => (
                       <div
                         key={channel.id}
                         className={`px-3 py-2 font-normal text-[16px] cursor-pointer flex items-center justify-between group transition-colors duration-200 ${
                          idx === selectedChannelSuggestionIndex 
                            ? isDark 
                              ? 'bg-[#cd6d2c] text-[#000000]' 
                              : 'bg-[#e9ecef] text-[#072837]'
                            : isDark 
                              ? 'bg-[#161515] hover:bg-[#292929] text-white' 
                              : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                        }`}
                         onMouseDown={() => handleChannelSuggestionClick(channel)}
                       >
                         <div className={`flex items-center gap-[8px] ${
                          idx === selectedChannelSuggestionIndex 
                            ? 'text-[#000000]' 
                            : isDark 
                              ? 'text-white group-hover:!text-white' 
                              : 'group-hover:!text-white text-[#072837]'
                        }`}>
                          {channel.name} <span className={`text-[16px] transition-colors duration-200 ${
                            idx === selectedChannelSuggestionIndex
                              ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                              : isDark ? 'text-[#b3b3b3] group-hover:!text-white' : 'group-hover:!text-white text-[#686868]'
                          }`}>({channel.send_as})</span>
                        </div>
                       </div>
                     ))}
                   </div>
                 )}
               </div>
             </div>
           )}
        </div>
      )}

      {/* Confirmation message */}
      <div className={`text-[14px] font-normal transition-colors duration-200 ${
        isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
      }`}>
        A confirmation email will be sent once issued.
      </div>
    </div>
  );
};

export default ManagerEmailSection;

